import { DeleteFive, Plus, TableReport } from '@icon-park/react'

import { Tokens } from '@/assets/tokens'

import { useRouting } from '@/hooks'

import { Button, Divider, Flex, Icon } from '@/components'
import { Layout } from '@/components/layout'

import { Paginator, SkeletonLoader } from '@/components/derived'
import { PageTitle } from '@/components/derived/PageTitle'
import { Routes } from '@/configs'
import { Alert, Confirmation } from '@/modules'
import { ReportStatus } from '@shared/models'
import { CompanyDetailsContextProvider, useCompanyContext } from '../CompanyDetails/CompanyDetails.context'
import { Filters } from './components/Filters/Filters.component'
import { NewReportFormModal, NewReportModalName } from './components/NewReportForm.component'
import { ReportItem } from './components/ReportItem.component'
import { ReportsContextProvider, useReportsContext } from './Reports.context'
import { CancelButton, ItemsContainer, NewReportButton, ReportsContainer, SelectButton } from './Reports.styles'
import { useCallback, useState } from 'react'

export function Reports() {
  return (
    <CompanyDetailsContextProvider>
      <ReportsContextProvider>
        <ReportsComponent />
      </ReportsContextProvider>
    </CompanyDetailsContextProvider>
  )
}

export function ReportsComponent(): JSX.Element {
  const { companyID, changeQueryParam, navigate } = useRouting()
  const {
    loadingReports,
    reportItems,
    reports,
    selectedReportIds,
    toggleReportSelection,
    clearSelection,
    batchDeleteReports,
    isDeletingBatch,
    selectAllVisible,
    toggleSelectAllVisible,
  } = useReportsContext()
  const { isLoadingCompany, company } = useCompanyContext()
  const sortedReportItems = reportItems.sort((a, b) => {
    const priorityOrder: Record<ReportStatus, number> = {
      [ReportStatus.STARTED]: 1,
      [ReportStatus.UPLOADED]: 2,
      [ReportStatus.FINISHED]: 3,
      [ReportStatus.ERRORED]: 3,
    }

    return priorityOrder[a.status] - priorityOrder[b.status]
  })

  const { sendAlert } = Alert.Hook()

  const { open: openDeleteConfirmation } = Confirmation.Hook({
    icon: DeleteFive,
    text: `Esta ação irá excluir permanentemente os ${selectedReportIds.size} reports selecionados. Tem certeza que deseja apagar os reports?`,
    confirmText: 'Sim! Apagar reports',
  })

  const [isSelectionModeActive, setSelectionModeActive] = useState(false)

  function openFormModal(): void {
    changeQueryParam({
      param: 'modal',
      value: NewReportModalName,
      replaceAll: true,
    })
  }

  if (company && !company.healthCheck!.isHealthy) {
    sendAlert({
      type: 'error',
      title: 'Erro no manual',
      message: 'Empresa está com manual incompleto: Favor revisar o manual!',
    })
    navigate(Routes.CompanyDetails.path, companyID!)
  }

  const handleBatchDelete = useCallback(() => {
    openDeleteConfirmation(batchDeleteReports)
  }, [openDeleteConfirmation, batchDeleteReports])

  const handleCancelSelection = () => {
    setSelectionModeActive(false)
    clearSelection()
  }

  const SelectionHeader = () => (
    <Flex $type="row" $justify="flex-end" $align="center" style={{ width: '100%' }}>
      {!isSelectionModeActive ?
        <Flex $type="row" $align="center">
          <SelectButton $variant="link" onClick={() => setSelectionModeActive(true)}>
            <Button.Text value="Selecionar" />
          </SelectButton>
          <span style={{ color: Tokens.ColorUIPrimary }}>|</span>
          <SelectButton
            $variant="link"
            onClick={() => {
              setSelectionModeActive(true)
              if (!selectAllVisible) {
                toggleSelectAllVisible()
              }
            }}>
            <Button.Text value="Selecionar todos" />
          </SelectButton>
        </Flex>
      : <Flex $type="row" $justify="flex-end" $align="center" style={{ width: '100%' }}>
          <Flex $type="row" $align="center" $gap={4}>
            <Button.Root
              $variant="link"
              onClick={handleBatchDelete}
              disabled={selectedReportIds.size === 0}
              $loading={isDeletingBatch}
              title={
                selectedReportIds.size === 0 ?
                  'Selecione reports para excluir'
                : `Excluir ${selectedReportIds.size} reports selecionados`
              }
              style={{
                minWidth: 'auto',
                width: '24px',
                height: '24px',
                padding: 0,
                borderRadius: '4px',
              }}>
              <Button.Icon
                value={DeleteFive}
                color={selectedReportIds.size === 0 ? Tokens.ColorTextTertiary : Tokens.ColorUIDanger}
              />
            </Button.Root>
            <CancelButton $variant="link" onClick={handleCancelSelection}>
              <Button.Text value="Cancelar" />
            </CancelButton>
          </Flex>
        </Flex>
      }
    </Flex>
  )

  return (
    <CompanyDetailsContextProvider>
      <Layout.Root>
        <ReportsContainer>
          <PageTitle
            title="Relatórios"
            subtitle="Gerencie e visualize os relatórios da empresa"
            icon={TableReport}
            backToName={company?.name}
            onBackClick={() => navigate(Routes.CompanyDetails.path, companyID!)}
          />
          <Filters />
          <Divider.Root>
            <Divider.Title value="Histórico de Relatórios" />
            <Divider.Spacer />
            <Divider.Subtitle value={`${reports?.elements.length ?? 0} Relatórios`} />
          </Divider.Root>
          <span>Pressione F5 para atualizar a situação do processamento.</span>
          <SelectionHeader />
          <ItemsContainer>
            {isLoadingCompany || loadingReports ?
              <SkeletonLoader />
            : <>
                <NewReportButton onClick={openFormModal}>
                  <Icon value={Plus} color={Tokens.ColorWhite} size={16} />
                  <span>Gerar Relatório</span>
                </NewReportButton>
                {sortedReportItems?.map((element) => (
                  <ReportItem
                    key={element.id}
                    report={element}
                    isSelectionModeActive={isSelectionModeActive}
                    isSelected={selectedReportIds.has(`${element.id}_${companyID}`)}
                    onToggleSelection={() => toggleReportSelection(element.id!, companyID!)}
                  />
                ))}
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    width: '100%',
                    margin: '1rem 0',
                  }}>
                  <Paginator totalPages={reports?.pages} />
                </div>
              </>
            }
          </ItemsContainer>
          <NewReportFormModal />
        </ReportsContainer>
      </Layout.Root>
    </CompanyDetailsContextProvider>
  )
}
